import { type ReactNode } from "react";
import { REC_CITATION_RE } from "./provenance";

// Per-record gate results for a cited chip (spanning answer). Local shape to
// avoid a cross-module type dependency; structurally matches api.CitationVerification.
interface CiteVerification {
  citation_ok: boolean;
  quote_ok: boolean;
  date_ok: boolean;
}

export interface FootnoteOpts {
  numbering: Map<string, number>;
  // Tooltip text for a cited record (date — subject [— ⚠ failure reasons]).
  tooltip?: (recordId: string) => string | undefined;
  // Click handler: scroll-to in source view, or navigate-to in normal view.
  onClick?: (recordId: string) => void;
  activeRecordId?: string | null;
  keyPrefix?: string;
  // Optional per-citation verification: a record with any failing gate renders
  // the existing footnote chip in the warning treatment plus a ⚠ glyph, so the
  // user can click straight to the source. Absent → plain provenance marks.
  verification?: (recordId: string) => CiteVerification | undefined;
  // When the user hits "show", emphasize the flagged chips (scroll target).
  emphasizeFlagged?: boolean;
}

// Render text, replacing each [rec:ID] token with a subtle gray
// superscript footnote number (¹ ² ³). Used in BOTH normal and source
// views so citations are always visible. Returns ReactNode[].
export function renderFootnotes(text: string, opts: FootnoteOpts): ReactNode[] {
  const {
    numbering,
    tooltip,
    onClick,
    activeRecordId,
    keyPrefix = "fn",
    verification,
    emphasizeFlagged = false,
  } = opts;
  const out: ReactNode[] = [];
  let last = 0;
  let i = 0;
  REC_CITATION_RE.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = REC_CITATION_RE.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index));
    const id = m[1]!;
    const n = numbering.get(id) ?? 0;
    const v = verification?.(id);
    const flagged = !!v && (!v.citation_ok || !v.quote_ok || !v.date_ok);
    const cls =
      `cite-ref${activeRecordId === id ? " active" : ""}` +
      (flagged ? " cite-ref-flagged" : "") +
      (flagged && emphasizeFlagged ? " cite-ref-emph" : "");
    out.push(
      <button
        key={`${keyPrefix}-${i}`}
        type="button"
        className={cls}
        title={tooltip?.(id)}
        aria-label={flagged ? `Source ${n} — needs checking` : `Source ${n}`}
        onClick={(e) => {
          e.stopPropagation();
          onClick?.(id);
        }}
      >
        <sup>
          {n}
          {flagged ? (
            <span className="cite-ref-warn" aria-hidden>
              ⚠
            </span>
          ) : null}
        </sup>
      </button>,
    );
    last = m.index + m[0].length;
    i++;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}
